import type {
  AdminAdapter,
  AdminAiSettings,
  AdminAiUsage,
  AdminCustomer,
  AdminCustomerDetail,
  AdminOverview,
  PlatformSettings,
  AddServerInput,
  AiStatus,
  BackupRecord,
  BackupSchedule,
  CertificatesView,
  CompatibilityEntry,
  CreateDeploymentInput,
  CredentialView,
  DeploymentSummary,
  Finding,
  IncidentsView,
  MonitoringView,
  NotificationEventName,
  PanelCatalogEntry,
  PanelInstallation,
  PlanView,
  Server,
  Session,
  StepView,
  TelegramView,
  TopologyPresetEntry,
  TunnelCatalogEntry,
} from './types.ts';

/**
 * The only place this application talks to the server.
 *
 * Three things it takes care of so no caller has to. The session cookie is
 * `HttpOnly`, so `credentials: 'same-origin'` is what carries it and the
 * JavaScript never sees the token. The CSRF token is attached to every
 * state-changing request from a single place. And a failed response is turned
 * into a typed error carrying the message the server wrote for the customer,
 * rather than a status code the interface then has to interpret.
 *
 * That message arrives in both languages. It used to arrive typed `never`,
 * which is a field that can never hold anything: the one route that already
 * sent Persian had it thrown away on the way in, and every screen rendered
 * `error.message`, so a Persian customer who mistyped a password was told
 * "That email address or password is not right."
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly localized?: { readonly en: string; readonly fa: string },
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuthentication(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/** Raised when the request never reached the server at all. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach the server');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

type Listener = () => void;

export class ApiClient {
  private csrfToken: string | undefined;
  private readonly onUnauthenticated = new Set<Listener>();

  constructor(private readonly baseUrl = '') {}

  /** Notified when the server says the session is gone, from any request. */
  subscribeUnauthenticated(listener: Listener): () => void {
    this.onUnauthenticated.add(listener);
    return () => this.onUnauthenticated.delete(listener);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: { signal?: AbortSignal } = {},
  ): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (method !== 'GET' && this.csrfToken) headers['x-csrf-token'] = this.csrfToken;

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        // The cookie is the credential; nothing is kept in JavaScript.
        credentials: 'same-origin',
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: options.signal,
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') throw error;
      throw new NetworkError(error);
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const payload: unknown = text ? safeParse(text) : undefined;

    if (!response.ok) {
      const error = (
        payload as {
          error?: {
            code?: string;
            message?: string;
            localized?: { en: string; fa: string };
          };
        }
      )?.error;
      if (response.status === 401) {
        this.csrfToken = undefined;
        for (const listener of this.onUnauthenticated) listener();
      }
      throw new ApiError(
        response.status,
        error?.code ?? `http_${response.status}`,
        error?.message ?? 'The request could not be completed.',
        error?.localized,
      );
    }

    return payload as T;
  }

  // ---- authentication --------------------------------------------------

  private adoptSession(session: Session): Session {
    this.csrfToken = session.csrfToken;
    return session;
  }

  async signIn(email: string, password: string): Promise<Session> {
    return this.adoptSession(
      await this.request<Session>('POST', '/api/auth/login', { email, password }),
    );
  }

  async register(input: {
    email: string;
    password: string;
    displayName: string;
    organizationName: string;
    locale: string;
  }): Promise<Session> {
    return this.adoptSession(await this.request<Session>('POST', '/api/auth/register', input));
  }

  async currentSession(): Promise<Session | undefined> {
    try {
      return this.adoptSession(await this.request<Session>('GET', '/api/auth/me'));
    } catch (error) {
      // Not being signed in is the normal case on a first visit, not a failure.
      if (error instanceof ApiError && error.isAuthentication) return undefined;
      throw error;
    }
  }

  // ---- platform administration -------------------------------------------
  //
  // Their own prefix, and every one of them 403s for an account that is not a
  // platform admin. The console hides them, but hiding is a courtesy: the
  // refusal that counts is the server's.

  async adminOverview(): Promise<AdminOverview> {
    return this.request('GET', '/api/admin/overview');
  }

  async adminCustomers(search?: string): Promise<{ customers: AdminCustomer[] }> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request('GET', `/api/admin/organizations${query}`);
  }

  async adminCustomer(id: string): Promise<AdminCustomerDetail> {
    return this.request('GET', `/api/admin/organizations/${encodeURIComponent(id)}`);
  }

  async adminSuspend(id: string, reason: string): Promise<{ status: string }> {
    return this.request('POST', `/api/admin/organizations/${encodeURIComponent(id)}/suspend`, {
      reason,
    });
  }

  async adminReactivate(id: string, reason: string): Promise<{ status: string }> {
    return this.request('POST', `/api/admin/organizations/${encodeURIComponent(id)}/reactivate`, {
      reason,
    });
  }

  async adminSetDeployments(
    id: string,
    enabled: boolean,
    reason: string,
  ): Promise<{ deploymentsEnabled: boolean }> {
    return this.request('POST', `/api/admin/organizations/${encodeURIComponent(id)}/deployments`, {
      enabled,
      reason,
    });
  }

  async adminRevokeSessions(id: string, reason: string): Promise<{ revoked: number }> {
    return this.request(
      'POST',
      `/api/admin/organizations/${encodeURIComponent(id)}/sessions/revoke`,
      { reason },
    );
  }

  async adminList(
    what:
      | 'users'
      | 'servers'
      | 'deployments'
      | 'installations'
      | 'tunnels'
      | 'backups'
      | 'incidents'
      | 'activity',
  ): Promise<Record<string, Record<string, unknown>[]>> {
    return this.request('GET', `/api/admin/${what}`);
  }

  /** What this build can install, read from the registry rather than a list. */
  async adminAdapters(): Promise<{ adapters: AdminAdapter[] }> {
    return this.request('GET', '/api/admin/adapters');
  }

  async adminRecheck(id: string): Promise<{ requested: boolean }> {
    return this.request(
      'POST',
      `/api/admin/organizations/${encodeURIComponent(id)}/health-recheck`,
      {},
    );
  }

  async adminSetMuted(
    id: string,
    muted: readonly string[],
    reason: string,
  ): Promise<{ muted: string[] }> {
    return this.request(
      'POST',
      `/api/admin/organizations/${encodeURIComponent(id)}/notifications`,
      {
        muted,
        reason,
      },
    );
  }

  /** What the AI is doing and what it is costing. Never the key. */
  async adminAi(): Promise<AdminAiUsage> {
    return this.request('GET', '/api/admin/ai');
  }

  async adminAiSettings(): Promise<AdminAiSettings> {
    return this.request('GET', '/api/admin/ai/settings');
  }

  /**
   * Save the AI configuration.
   *
   * A key is sent only when the operator typed one. Absent means "leave what
   * is stored alone", which is the only sensible default for a value they are
   * not allowed to read back; an empty string is the explicit removal.
   */
  async adminSaveAiSettings(payload: {
    settings: AdminAiSettings['settings'];
    localApiKey?: string;
    claudeApiKey?: string;
  }): Promise<Pick<AdminAiSettings, 'settings' | 'updatedAt' | 'credentials'>> {
    return this.request('PUT', '/api/admin/ai/settings', payload);
  }

  async adminTestAi(
    provider: 'local' | 'claude',
  ): Promise<{ available: boolean; detail?: string }> {
    return this.request('POST', '/api/admin/ai/test', { provider });
  }

  async adminSettings(): Promise<{
    settings: PlatformSettings;
    availablePanelAdapters: string[];
    availableTunnelAdapters: string[];
  }> {
    return this.request('GET', '/api/admin/settings');
  }

  async adminSaveSettings(settings: PlatformSettings): Promise<{ settings: PlatformSettings }> {
    return this.request('PUT', '/api/admin/settings', { settings });
  }

  async signOut(): Promise<void> {
    await this.request('POST', '/api/auth/logout');
    this.csrfToken = undefined;
  }

  // ---- catalog ---------------------------------------------------------

  async panels(): Promise<readonly PanelCatalogEntry[]> {
    return (await this.request<{ panels: PanelCatalogEntry[] }>('GET', '/api/catalog/panels'))
      .panels;
  }

  async tunnels(): Promise<readonly TunnelCatalogEntry[]> {
    return (await this.request<{ tunnels: TunnelCatalogEntry[] }>('GET', '/api/catalog/tunnels'))
      .tunnels;
  }

  async topologies(): Promise<readonly TopologyPresetEntry[]> {
    return (
      await this.request<{ topologies: TopologyPresetEntry[] }>('GET', '/api/catalog/topologies')
    ).topologies;
  }

  async compatibility(): Promise<readonly CompatibilityEntry[]> {
    return (
      await this.request<{ combinations: CompatibilityEntry[] }>(
        'GET',
        '/api/catalog/compatibility',
      )
    ).combinations;
  }

  // ---- servers ---------------------------------------------------------

  async servers(): Promise<readonly Server[]> {
    return (await this.request<{ servers: Server[] }>('GET', '/api/servers')).servers;
  }

  async server(id: string): Promise<{ server: Server; inspection?: unknown }> {
    return this.request('GET', `/api/servers/${encodeURIComponent(id)}`);
  }

  async addServer(input: AddServerInput): Promise<{ server: Server; inspectionJobId: string }> {
    return this.request('POST', '/api/servers', input);
  }

  async inspectServer(id: string): Promise<{ jobId: string }> {
    return this.request('POST', `/api/servers/${encodeURIComponent(id)}/inspect`);
  }

  async removeServer(id: string): Promise<{ message: string }> {
    return this.request('DELETE', `/api/servers/${encodeURIComponent(id)}`);
  }

  // ---- deployments -----------------------------------------------------

  async deployments(): Promise<readonly DeploymentSummary[]> {
    return (await this.request<{ deployments: DeploymentSummary[] }>('GET', '/api/deployments'))
      .deployments;
  }

  async createDeployment(input: CreateDeploymentInput): Promise<{
    deployment: { id: string; status: string };
    plan: PlanView;
    findings: readonly Finding[];
  }> {
    return this.request('POST', '/api/deployments', input);
  }

  async deployment(id: string): Promise<{
    deployment: DeploymentSummary;
    plan?: PlanView;
    steps: readonly StepView[];
  }> {
    return this.request('GET', `/api/deployments/${encodeURIComponent(id)}`);
  }

  async approveDeployment(id: string, planHash: string): Promise<{ jobId: string }> {
    return this.request('POST', `/api/deployments/${encodeURIComponent(id)}/approve`, { planHash });
  }

  async cancelDeployment(id: string): Promise<{ message: string }> {
    return this.request('POST', `/api/deployments/${encodeURIComponent(id)}/cancel`);
  }

  async credentials(deploymentId: string): Promise<readonly CredentialView[]> {
    return (
      await this.request<{ credentials: CredentialView[] }>(
        'GET',
        `/api/deployments/${encodeURIComponent(deploymentId)}/credentials`,
      )
    ).credentials;
  }

  /**
   * Spend a one-time link from the bot.
   *
   * No session and no CSRF token: the token in the path is the whole of the
   * authorization, which is why the server makes it short-lived, single-use
   * and stored only as a hash. The request itself is what spends it, so this
   * is never retried.
   */
  async redeemAccessLink(token: string): Promise<readonly CredentialView[]> {
    return (
      await this.request<{ credentials: CredentialView[] }>(
        'GET',
        `/api/access/${encodeURIComponent(token)}`,
      )
    ).credentials;
  }

  async revealCredential(deploymentId: string, credentialId: string): Promise<CredentialView> {
    return (
      await this.request<{ credential: CredentialView }>(
        'POST',
        `/api/deployments/${encodeURIComponent(deploymentId)}/credentials/${encodeURIComponent(credentialId)}/reveal`,
      )
    ).credential;
  }

  // ---- what is installed, and how it is doing -------------------------

  /**
   * The panels this account actually has installed.
   *
   * Named apart from `panels()`, which lists the ones that *can* be installed.
   * Two methods called `panels` would be one method, and the one that survived
   * would be whichever was declared last.
   */
  async installedPanels(): Promise<readonly PanelInstallation[]> {
    return (await this.request<{ panels: PanelInstallation[] }>('GET', '/api/panels')).panels;
  }

  async monitoring(): Promise<MonitoringView> {
    return this.request('GET', '/api/monitoring');
  }

  async incidents(): Promise<IncidentsView> {
    return this.request('GET', '/api/incidents');
  }

  /** Only answered for accounts permitted to read diagnostics. */
  async aiStatus(): Promise<AiStatus> {
    return this.request('GET', '/api/ai');
  }

  // ---- backups ---------------------------------------------------------

  async backups(installationId?: string): Promise<{
    backups: readonly BackupRecord[];
    schedules: readonly BackupSchedule[];
  }> {
    const query = installationId ? `?installationId=${encodeURIComponent(installationId)}` : '';
    return this.request('GET', `/api/backups${query}`);
  }

  /** One backup, now. */
  async runBackup(installationId: string): Promise<{ jobId: string; status: string }> {
    return this.request('POST', '/api/backups', { installationId });
  }

  /**
   * Turn scheduled backups on.
   *
   * Separate from `runBackup` although the endpoint is shared, because they
   * are different decisions: one takes a copy, the other commits the platform
   * to taking copies. Nothing calls this except a customer pressing a switch.
   */
  async scheduleBackups(
    installationId: string,
    everyHours: number,
    keep?: number,
  ): Promise<{ jobId: string; status: string }> {
    return this.request('POST', '/api/backups', {
      installationId,
      everyHours,
      ...(keep === undefined ? {} : { keep }),
    });
  }

  async cancelBackupSchedule(installationId: string): Promise<{ cancelled: number }> {
    return this.request('DELETE', `/api/backups/schedule/${encodeURIComponent(installationId)}`);
  }

  async verifyBackup(id: string): Promise<{ verified: boolean; reason?: string }> {
    return this.request('POST', `/api/backups/${encodeURIComponent(id)}/verify`);
  }

  async restoreBackup(id: string): Promise<{ jobId: string; status: string }> {
    // `confirmed` is the server's requirement, not a formality this client
    // fills in: the screen asks the customer first and only then gets here.
    return this.request('POST', `/api/backups/${encodeURIComponent(id)}/restore`, {
      confirmed: true,
    });
  }

  // ---- certificates ----------------------------------------------------

  async certificates(): Promise<CertificatesView> {
    return this.request('GET', '/api/certificates');
  }

  async saveDnsCredential(
    apiToken: string,
    zone?: string,
  ): Promise<{ provider: string; zone: string | null; verified: boolean }> {
    return this.request('PUT', '/api/certificates/dns-credential', {
      provider: 'dns-cloudflare',
      apiToken,
      ...(zone ? { zone } : {}),
    });
  }

  async requestCertificate(input: {
    installationId: string;
    domains: readonly string[];
    email: string;
    production: boolean;
  }): Promise<{ jobId: string; status: string }> {
    return this.request('POST', '/api/certificates', input);
  }

  // ---- notifications ---------------------------------------------------

  async telegram(): Promise<TelegramView> {
    return this.request('GET', '/api/telegram');
  }

  async telegramLinkCode(language: string): Promise<{ code: string; expiresAt: string }> {
    return this.request('POST', '/api/telegram/link-code', { language });
  }

  async unlinkTelegramChat(chatId: string): Promise<{ unlinked: boolean }> {
    return this.request('DELETE', `/api/telegram/chats/${encodeURIComponent(chatId)}`);
  }

  async saveNotificationPreferences(
    muted: readonly NotificationEventName[],
  ): Promise<{ muted: readonly NotificationEventName[] }> {
    return this.request('PUT', '/api/telegram/preferences', { muted });
  }

  /**
   * Live deployment progress.
   *
   * `EventSource` reconnects by itself and sends `Last-Event-ID`, which the
   * server uses as a replay cursor — so a customer whose train goes into a
   * tunnel comes back to the same screen with nothing missing.
   */
  watchDeployment(
    id: string,
    handlers: {
      onState?: (state: { deployment: DeploymentSummary; steps: readonly StepView[] }) => void;
      onProgress?: (event: DeploymentProgressEvent) => void;
      onFinished?: (deployment: DeploymentSummary) => void;
      onError?: () => void;
    },
  ): () => void {
    const source = new EventSource(
      `${this.baseUrl}/api/deployments/${encodeURIComponent(id)}/events`,
      { withCredentials: true },
    );

    const parse = <T>(event: MessageEvent<string>): T | undefined => safeParse(event.data) as T;

    source.addEventListener('state', (event) => {
      const state = parse<{ deployment: DeploymentSummary; steps: readonly StepView[] }>(
        event as MessageEvent<string>,
      );
      if (state) handlers.onState?.(state);
    });
    source.addEventListener('progress', (event) => {
      const payload = parse<DeploymentProgressEvent>(event as MessageEvent<string>);
      if (payload) handlers.onProgress?.(payload);
    });
    source.addEventListener('finished', (event) => {
      const deployment = parse<DeploymentSummary>(event as MessageEvent<string>);
      if (deployment) handlers.onFinished?.(deployment);
      source.close();
    });
    source.onerror = () => handlers.onError?.();

    return () => source.close();
  }
}

/** The events the deployment executor emits, as the stream delivers them. */
export type DeploymentProgressEvent =
  | { type: 'deployment.status'; at: string; status: string; phase?: string }
  | {
      type: 'step.status';
      at: string;
      stepKey: string;
      ordinal: number;
      title: { en: string; fa: string };
      phase: string;
      status: string;
      attempt: number;
      durationMs?: number;
      errorCode?: string;
      errorMessage?: { en: string; fa: string };
    }
  | {
      type: 'step.progress';
      at: string;
      stepKey: string;
      message: { en: string; fa: string };
      fraction?: number;
    }
  | { type: 'deployment.progress'; at: string; completed: number; total: number; fraction?: number }
  | { type: 'health.result'; at: string; scope: string; result: unknown }
  | { type: 'rollback.progress'; at: string; undone: number; total: number; detail: string }
  | {
      type: 'deployment.finished';
      at: string;
      status: string;
      message: { en: string; fa: string };
    };

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // A non-JSON body from a proxy or an error page is not worth throwing over;
    // the status code already says what happened.
    return undefined;
  }
}

export const api = new ApiClient();
